import routingService from 'common/src/modules/routing/routing.service';
import {RootState} from 'common/src/redux/store';
import {z} from 'zod';
import buildAppRoute from './utils/buildAppRoute';
import promptToRoute from './utils/promptToRoute';
import validateAppRoute from './utils/validateAppRoute';
import waitForNavigationCompletion from './utils/waitForNavigationCompletion';
import {ToolExecuteParams, ToolResult} from '../../copilot.types';
import toolResult from '../../utils/creators/toolResult';
import {logger} from '../../utils/logger';
import {ToolName} from '../../copilot.enums';
import tool from '../../utils/creators/tool';

const inputSchema = z.object({
  prompt: z.string().describe('Natural language navigation request'),
});

function generalNavigationTool(getState: () => RootState) {
  async function execute({
    input: {prompt},
    abortSignal,
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    try {
      const promptToRouteResult = await promptToRoute(prompt, abortSignal);
      const {routeKey, pathParamValues, queryParamValues} = promptToRouteResult;

      if (!promptToRouteResult.success || !routeKey) {
        const {missingInfo} = promptToRouteResult;

        return toolResult(
          `Could not navigate to the requested route. The following information was missing: ${missingInfo}`,
        );
      }

      const routeDef = routingService.getRouteDefinition(routeKey);

      const appRoute = buildAppRoute({routeDef, pathParamValues, queryParamValues});
      logger.log('AppRoute:', appRoute);

      const errorMessage = validateAppRoute({appRoute, routeDef});

      if (errorMessage) {
        return toolResult(`Error resolving route: ${errorMessage}`);
      }

      routingService.goTo(appRoute);

      const navigationResult = await waitForNavigationCompletion({
        getState,
        isNavigationComplete: routeDef.isNavigationComplete ?? (() => true),
      });

      const url = routingService.createUrl(appRoute);

      return toolResult(
        navigationResult.passed
          ? `Successfully navigated to ${url}`
          : `Navigation failed: ${navigationResult.reason}`,
      );
    } catch (error) {
      return toolResult(
        `Navigation failed: ${error instanceof Error ? error.message : 'Unknown error occurred'}`,
      );
    }
  }

  return tool({
    name: ToolName.GeneralNavigation,
    displayName: 'Navigating',
    description: 'Navigate to pages and routes using natural language',
    execute,
    inputSchema,
  });
}

export default generalNavigationTool;
